import { roleScopeLevelSchema } from '@btsis/core';
import { oc } from '@orpc/contract';
import { z } from 'zod';
import { commonErrors } from './common/errors.js';
import { bulkResultSchema, sortedListRequest } from './common/list.js';
import { paginated } from './common/pagination.js';
import { clearablePhoneSchema } from './common/phone.js';

const guarded = {
  UNAUTHENTICATED: commonErrors.UNAUTHENTICATED,
  PERMISSION_DENIED: commonErrors.PERMISSION_DENIED,
  TWO_FACTOR_REQUIRED: commonErrors.TWO_FACTOR_REQUIRED,
  OUT_OF_SCOPE: commonErrors.OUT_OF_SCOPE,
  NOT_FOUND: commonErrors.NOT_FOUND,
  CONFLICT: commonErrors.CONFLICT,
  VALIDATION_FAILED: commonErrors.VALIDATION_FAILED,
};

/**
 * Who wrote an assignment: an administrator (`manual`), or a campaign staff
 * assignment in force (`campaign_staff`) — the automatic *Operasyon
 * Sorumlusu*, always `fulfillment_manager` at `self`. An automatic one is a
 * label: it grants nothing, the role editor shows it read-only and never sends
 * it back, and it comes and goes with the assignments (docs/decisions/0096).
 */
export const userRoleOriginSchema = z.enum(['manual', 'campaign_staff']);

export const userRoleAssignmentSchema = z.object({
  origin: userRoleOriginSchema,
  roleId: z.uuid(),
  roleCode: z.string(),
  roleName: z.string(),
  requiresTwoFactor: z.boolean(),
  scopeLevel: roleScopeLevelSchema,
  scopeId: z.uuid().nullable(),
  scopeName: z.string().nullable(),
});

export const userSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  email: z.string(),
  isActive: z.boolean(),
  twoFactorEnabled: z.boolean(),
  institutionId: z.uuid(),
  institutionName: z.string(),
  institutionCode: z.string(),
  countryId: z.uuid(),
  countryName: z.string(),
  zoneId: z.uuid(),
  zoneName: z.string(),
  regionId: z.uuid(),
  regionName: z.string(),
  lastLoginAt: z.string().nullable(),
  createdAt: z.string(),
  /**
   * As an administrator typed it, and its E.164 form. Unmasked, unlike a
   * donor's or a reference person's: this screen is where the number is **set**,
   * behind `user.list`, and an administrator who cannot read back what they
   * typed cannot confirm it. `phoneE164` is what the WhatsApp intake resolves a
   * sender against — docs/decisions/0065.
   */
  phone: z.string().nullable(),
  phoneE164: z.string().nullable(),
  telegramChatId: z.string().nullable(),
  roles: z.array(userRoleAssignmentSchema),
  /**
   * Whether the caller may write to this user at all. False when the user
   * holds a permission the caller does not, so a screen can lock the edit,
   * invite and deactivate actions instead of offering a write the server will
   * refuse. Visibility is unaffected: a locked row is still listed and read.
   */
  manageable: z.boolean(),
  /**
   * Whether the caller may open a support session as this user: an active
   * global support administrator, a different active user, and a target whose
   * permissions are strictly below the caller's and include no
   * `user.impersonate` — docs/decisions/0113 and its amendment. The start route
   * asks the same rule again and names the reason it refuses.
   */
  impersonable: z.boolean(),
});

export const roleOptionSchema = z.object({
  id: z.uuid(),
  code: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  requiresTwoFactor: z.boolean(),
  /**
   * Whether the caller may hand this role out at all. False when the role
   * carries a permission the caller does not hold, so the form can lock the
   * option instead of offering a grant the server will refuse.
   */
  grantable: z.boolean(),
});

/**
 * A user may hold the same role at several places in the hierarchy, so an
 * assignment is a pair: which role, and how far it reaches. `scopeId` is null
 * only for the global level and for `self`, which reaches the user's own
 * records and names no node (docs/decisions/0087).
 */
export const roleAssignmentInputSchema = z.object({
  roleId: z.uuid(),
  scopeLevel: roleScopeLevelSchema,
  scopeId: z.uuid().nullable().default(null),
});

export const activationResultSchema = z.object({
  id: z.uuid(),
  ok: z.boolean(),
  code: z.string().nullable(),
  revokedSessions: z.number().int().min(0),
});

export type RoleOption = z.infer<typeof roleOptionSchema>;
export type User = z.infer<typeof userSchema>;
export type UserRoleAssignment = z.infer<typeof userRoleAssignmentSchema>;
export type UserRoleOrigin = z.infer<typeof userRoleOriginSchema>;
export type RoleAssignmentInput = z.infer<typeof roleAssignmentInputSchema>;

/**
 * The columns `user.list` orders by: each is one non-null, indexed column on
 * `users`. The institution name is absent because ordering by it sorts the
 * joined row rather than the one the page is drawn from, and `last_login_at`
 * because it is nullable — a descending "most recent login" would lead with
 * every account that has never signed in, and ordering it the other way needs
 * an index this list does not have.
 */
export const USER_SORT_FIELDS = ['name', 'email', 'createdAt'] as const;

export type UserSortField = (typeof USER_SORT_FIELDS)[number];

export const userNameSchema = z.string().trim().min(2).max(200);
export const userEmailSchema = z.email().trim().max(320);
/** A Telegram chat identity: digits, no leading zero; the empty string clears it. */
export const userTelegramChatIdSchema = z.string().regex(/^(?:[1-9][0-9]{0,15})?$/);
const nameSchema = userNameSchema;
const emailSchema = userEmailSchema;
/**
 * **International form only**, as every telephone the API takes
 * (docs/decisions/0086): a national number would need a country to normalize,
 * and guessing one would silently map a number to the wrong country on a field
 * that authorizes donation entry (docs/decisions/0065). Blank clears it.
 */
const phoneSchema = clearablePhoneSchema;

export const userContract = {
  list: oc
    .route({
      method: 'GET',
      path: '/users',
      summary: 'Users visible within the caller’s scope',
    })
    .errors(guarded)
    .input(
      sortedListRequest(USER_SORT_FIELDS, 'name', 'asc').extend({
        institutionId: z.uuid().optional(),
        roleId: z.uuid().optional(),
        status: z.enum(['all', 'active', 'inactive']).default('all'),
      }),
    )
    .output(paginated(userSchema)),

  get: oc
    .route({ method: 'GET', path: '/users/{id}' })
    .errors(guarded)
    .input(z.object({ id: z.uuid() }))
    .output(z.object({ user: userSchema })),

  /** Roles the caller may hand out, so the form offers real choices. */
  roleOptions: oc
    .route({ method: 'GET', path: '/users/role-options' })
    .errors(guarded)
    .input(z.object({}))
    .output(z.object({ items: z.array(roleOptionSchema) })),

  create: oc
    .route({ method: 'POST', path: '/users' })
    .errors(guarded)
    .input(
      z.object({
        institutionId: z.uuid(),
        name: nameSchema,
        email: emailSchema,
        phone: phoneSchema.optional(),
        telegramChatId: userTelegramChatIdSchema.optional(),
        roles: z.array(roleAssignmentInputSchema).max(20).default([]),
        sendInvite: z.boolean().default(true),
      }),
    )
    .output(
      z.object({
        user: userSchema,
        invited: z.boolean(),
        /**
         * The password-setup link itself, for the administrator to hand over
         * when mail cannot carry it — docs/plans/09-legacy-parity-integrations.md
         * §9.1. It is answered **once**, on the response of the call that minted
         * it, and is never stored, listed or re-readable: a second copy means a
         * second invitation, which spends the first. `null` when none was minted.
         */
        invitationUrl: z.string().nullable(),
      }),
    ),

  update: oc
    .route({ method: 'PATCH', path: '/users/{id}' })
    .errors(guarded)
    .input(
      z.object({
        id: z.uuid(),
        name: nameSchema.optional(),
        email: emailSchema.optional(),
        /** An empty string clears the number; absent leaves it alone. */
        phone: phoneSchema.optional(),
        telegramChatId: userTelegramChatIdSchema.optional(),
        institutionId: z.uuid().optional(),
        roles: z.array(roleAssignmentInputSchema).max(20).optional(),
      }),
    )
    .output(z.object({ user: userSchema })),

  /**
   * Deactivation replaces deletion: a user who has touched a donation can never
   * be removed without breaking the audit trail. One endpoint serves both the
   * row action and the bulk toolbar, and reports a result per row.
   */
  setActive: oc
    .route({ method: 'POST', path: '/users/activation' })
    .errors(guarded)
    .input(
      z.object({
        ids: z.array(z.uuid()).min(1).max(100),
        isActive: z.boolean(),
      }),
    )
    .output(bulkResultSchema(activationResultSchema)),

  invite: oc
    .route({ method: 'POST', path: '/users/{id}/invite' })
    .errors(guarded)
    .input(z.object({ id: z.uuid() }))
    .output(z.object({ sent: z.boolean(), invitationUrl: z.string().nullable() })),

  /**
   * Clears another user's second factor — the secret and every recovery code —
   * ends all their sessions, and marks them to enrol again at their next
   * sign-in even if no role of theirs demands a second factor. A user outside
   * the caller's scope is `NOT_FOUND`; one above the caller's privilege
   * ceiling, the caller themself, an impersonated session and a user with no
   * second factor are refused. Audited as `user` / `two_factor_reset`; the user
   * is told by mail. Docs/decisions/0135.
   */
  resetTwoFactor: oc
    .route({ method: 'POST', path: '/users/{id}/two-factor/reset' })
    .errors(guarded)
    .input(z.object({ id: z.uuid() }))
    .output(z.object({ success: z.literal(true), revokedSessions: z.number().int().min(0) })),
};
